import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const MemberManagementSection = ({ formData, onUpdate, errors = {} }) => {
  const [contacts, setContacts] = useState(formData?.contacts || []);
  const [newContact, setNewContact] = useState({
    name: '',
    position: '',
    email: '',
    phone: '',
    isPrimary: false
  });
  const [activeLanguage, setActiveLanguage] = useState('ar');
  const [activeContentType, setActiveContentType] = useState('specialAchievements'); // 'specialAchievements', 'activitiesAndAchievements', 'currentAdministrativeTeam', 'notes'

  const languages = [
    { code: 'ar', label: 'العربية', flag: '🇸🇾', dir: 'rtl' },
    { code: 'en', label: 'English', flag: '🇺🇸', dir: 'ltr' },
    { code: 'tr', label: 'Türkçe', flag: '🇹🇷', dir: 'ltr' }
  ];

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
  };

  const handleContactChange = (field, value) => {
    setNewContact(prev => ({ ...prev, [field]: value }));
  };

  const addContact = () => {
    if (newContact?.name && newContact?.email) {
      const updatedContacts = [...contacts, { ...newContact, id: Date.now() }];
      setContacts(updatedContacts);
      onUpdate({ contacts: updatedContacts });
      setNewContact({
        name: '',
        position: '',
        email: '',
        phone: '',
        isPrimary: false
      });
    }
  };

  const removeContact = (contactId) => {
    const updatedContacts = contacts?.filter(contact => contact?.id !== contactId);
    setContacts(updatedContacts);
    onUpdate({ contacts: updatedContacts });
  };

  const setPrimaryContact = (contactId) => {
    const updatedContacts = contacts?.map(contact => ({
      ...contact,
      isPrimary: contact?.id === contactId
    }));
    setContacts(updatedContacts);
    onUpdate({ contacts: updatedContacts });
  };

  const membershipTypes = [
    { value: 'student', label: 'طالب - Student' },
    { value: 'graduate', label: 'خريج - Graduate' },
    { value: 'faculty', label: 'عضو هيئة تدريس - Faculty' },
    { value: 'supporter', label: 'مؤيد - Supporter' }
  ];

  const getCurrentContent = () => {
    const field = `${activeContentType}${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
    return formData?.[field] || '';
  };

  const handleContentChange = (language, value, type) => {
    const field = `${type}${language?.charAt(0)?.toUpperCase() + language?.slice(1)}`;
    onUpdate({ [field]: value });
  };

  const getContentPlaceholder = () => {
    const placeholders = {
      specialAchievements: {
        ar: 'أدخل الإنجازات الخاصة للاتحاد الفرعي...',
        en: 'Enter special achievements of the sub-union...',
        tr: 'Alt birliğin özel başarılarını girin...'
      },
      activitiesAndAchievements: {
        ar: 'أدخل الأنشطة والإنجازات...',
        en: 'Enter activities and achievements...',
        tr: 'Faaliyetler ve başarıları girin...'
      },
      currentAdministrativeTeam: {
        ar: 'أدخل معلومات الفريق الإداري الحالي...',
        en: 'Enter current administrative team information...',
        tr: 'Mevcut idari ekip bilgilerini girin...'
      },
      notes: {
        ar: 'أدخل ملاحظات إضافية...',
        en: 'Enter additional notes...',
        tr: 'Ek notlar girin...'
      }
    };
    return placeholders[activeContentType]?.[activeLanguage] || '';
  };

  const getContentLabel = () => {
    const labels = {
      specialAchievements: {
        ar: 'الإنجازات الخاصة',
        en: 'Special Achievements',
        tr: 'Özel Başarılar'
      },
      activitiesAndAchievements: {
        ar: 'الأنشطة والإنجازات',
        en: 'Activities and Achievements',
        tr: 'Faaliyetler ve Başarılar'
      },
      currentAdministrativeTeam: {
        ar: 'الفريق الإداري الحالي',
        en: 'Current Administrative Team',
        tr: 'Mevcut İdari Ekip'
      },
      notes: {
        ar: 'ملاحظات',
        en: 'Notes',
        tr: 'Notlar'
      }
    };
    return labels[activeContentType]?.[activeLanguage] || '';
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center space-x-3 pb-4 border-b border-border">
        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
          <Icon name="Users" size={18} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground font-arabic">
            إدارة الأعضاء والاتصال
          </h3>
          <p className="text-sm text-muted-foreground font-caption">
            إدارة معلومات الأعضاء وجهات الاتصال
          </p>
        </div>
      </div>
      {/* Member Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="UserCheck" size={20} className="text-success" />
            </div>
            <div>
              <Input
                label="العدد الإجمالي للأعضاء"
                type="number"
                placeholder="0"
                value={formData?.totalMembers || ''}
                onChange={(e) => handleInputChange('totalMembers', e?.target?.value)}
                error={errors?.totalMembers}
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Active Members - Commented out per user request */}
        {/* <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="UserPlus" size={20} className="text-accent" />
            </div>
            <div>
              <Input
                label="الأعضاء النشطون"
                type="number"
                placeholder="0"
                value={formData?.activeMembers || ''}
                onChange={(e) => handleInputChange('activeMembers', e?.target?.value)}
                error={errors?.activeMembers}
                min="0"
              />
            </div>
          </div>
        </div> */}

        {/* New Members This Month - Commented out per user request */}
        {/* <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={20} className="text-warning" />
            </div>
            <div>
              <Input
                label="أعضاء جدد هذا الشهر"
                type="number"
                placeholder="0"
                value={formData?.newMembersThisMonth || ''}
                onChange={(e) => handleInputChange('newMembersThisMonth', e?.target?.value)}
                error={errors?.newMembersThisMonth}
                min="0"
              />
            </div>
          </div>
        </div> */}
      </div>
      {/* Contact Information - Commented out per user request */}
      {/* <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-semibold text-foreground font-arabic">
            معلومات الاتصال
          </h4>
          <Button
            variant="outline"
            size="sm"
            onClick={addContact}
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
            disabled={!newContact?.name || !newContact?.email}
          >
            إضافة جهة اتصال
          </Button>
        </div>

        <div className="bg-muted/30 border border-border rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="الاسم الكامل *"
              type="text"
              placeholder="أدخل الاسم الكامل"
              value={newContact?.name}
              onChange={(e) => handleContactChange('name', e?.target?.value)}
              required
            />

            <Input
              label="المنصب"
              type="text"
              placeholder="مثال: رئيس الاتحاد"
              value={newContact?.position}
              onChange={(e) => handleContactChange('position', e?.target?.value)}
            />

            <Input
              label="البريد الإلكتروني *"
              type="email"
              placeholder="example@email.com"
              value={newContact?.email}
              onChange={(e) => handleContactChange('email', e?.target?.value)}
              required
            />

            <Input
              label="رقم الهاتف"
              type="tel"
              placeholder="+963 XX XXX XXXX"
              value={newContact?.phone}
              onChange={(e) => handleContactChange('phone', e?.target?.value)}
            />
          </div>

          <Checkbox
            label="جهة الاتصال الرئيسية"
            checked={newContact?.isPrimary}
            onChange={(e) => handleContactChange('isPrimary', e?.target?.checked)}
          />
        </div>

        {contacts?.length > 0 && (
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-foreground font-arabic">
              جهات الاتصال المضافة ({contacts?.length})
            </h5>
            
            <div className="space-y-2">
              {contacts?.map((contact) => (
                <div
                  key={contact?.id}
                  className={`bg-card border rounded-lg p-4 ${
                    contact?.isPrimary ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center space-x-2">
                        <h6 className="font-medium text-foreground">
                          {contact?.name}
                        </h6>
                        {contact?.isPrimary && (
                          <span className="px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full font-caption">
                            رئيسي
                          </span>
                        )}
                      </div>
                      
                      {contact?.position && (
                        <p className="text-sm text-muted-foreground font-caption">
                          {contact?.position}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <Icon name="Mail" size={14} className="text-muted-foreground" />
                          <span className="font-caption">{contact?.email}</span>
                        </div>
                        
                        {contact?.phone && (
                          <div className="flex items-center space-x-2">
                            <Icon name="Phone" size={14} className="text-muted-foreground" />
                            <span className="font-caption">{contact?.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {!contact?.isPrimary && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPrimaryContact(contact?.id)}
                          iconName="Star"
                          iconSize={14}
                          title="تعيين كجهة اتصال رئيسية"
                        />
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeContact(contact?.id)}
                        iconName="Trash2"
                        iconSize={14}
                        className="text-error hover:text-error"
                        title="حذف جهة الاتصال"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div> */}
      {/* Additional Settings - Commented out per user request */}
      {/* <div className="space-y-4">
        <h4 className="text-md font-semibold text-foreground font-arabic">
          إعدادات إضافية
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="الموقع الإلكتروني"
            type="url"
            placeholder="https://example.com"
            value={formData?.website || ''}
            onChange={(e) => handleInputChange('website', e?.target?.value)}
            error={errors?.website}
          />

          <Input
            label="صفحة الفيسبوك"
            type="url"
            placeholder="https://facebook.com/page"
            value={formData?.facebookPage || ''}
            onChange={(e) => handleInputChange('facebookPage', e?.target?.value)}
            error={errors?.facebookPage}
          />
        </div>

        <div className="space-y-3">
          <Checkbox
            label="السماح بالتسجيل الجديد"
            description="السماح للأعضاء الجدد بالتسجيل في هذا الاتحاد الفرعي"
            checked={formData?.allowNewRegistrations || false}
            onChange={(e) => handleInputChange('allowNewRegistrations', e?.target?.checked)}
          />

          <Checkbox
            label="إرسال إشعارات البريد الإلكتروني"
            description="إرسال إشعارات للأعضاء عند النشاطات والأحداث الجديدة"
            checked={formData?.emailNotifications || false}
            onChange={(e) => handleInputChange('emailNotifications', e?.target?.checked)}
          />

          <Checkbox
            label="عرض في الدليل العام"
            description="إظهار هذا الاتحاد الفرعي في دليل الاتحادات العام"
            checked={formData?.showInDirectory || true}
            onChange={(e) => handleInputChange('showInDirectory', e?.target?.checked)}
          />
        </div>
      </div> */}

      {/* Additional Content Sections */}
      <div className="space-y-6 pt-6 border-t border-border">
        <div className="flex items-center space-x-3 pb-4">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={18} color="white" />
          </div>
          <div>
            <h4 className="text-md font-semibold text-foreground font-arabic">
              محتوى إضافي
            </h4>
            <p className="text-sm text-muted-foreground font-caption">
              أضف معلومات إضافية عن الاتحاد الفرعي
            </p>
          </div>
        </div>

        {/* Content Type Selector */}
        <div className="flex items-center space-x-2 bg-muted rounded-lg p-1">
          <Button
            variant={activeContentType === 'specialAchievements' ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setActiveContentType('specialAchievements')}
          >
            الإنجازات الخاصة
          </Button>
          <Button
            variant={activeContentType === 'activitiesAndAchievements' ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setActiveContentType('activitiesAndAchievements')}
          >
            الأنشطة والإنجازات
          </Button>
          <Button
            variant={activeContentType === 'currentAdministrativeTeam' ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setActiveContentType('currentAdministrativeTeam')}
          >
            الفريق الإداري
          </Button>
          <Button
            variant={activeContentType === 'notes' ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setActiveContentType('notes')}
          >
            ملاحظات
          </Button>
        </div>

        {/* Language Tabs */}
        <div className="flex items-center space-x-2 bg-muted rounded-lg p-1">
          {languages?.map((language) => (
            <Button
              key={language?.code}
              variant={activeLanguage === language?.code ? 'default' : 'ghost'}
              size="sm"
              className="flex-1"
              onClick={() => setActiveLanguage(language?.code)}
            >
              <span className="mr-2">{language?.flag}</span>
              {language?.label}
            </Button>
          ))}
        </div>

        {/* Content Editor */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground font-arabic">
            {getContentLabel()}
          </label>
          <textarea
            className={`w-full h-48 p-4 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
              languages?.find(l => l?.code === activeLanguage)?.dir === 'rtl' ? 'text-right' : 'text-left'
            }`}
            dir={languages?.find(l => l?.code === activeLanguage)?.dir}
            placeholder={getContentPlaceholder()}
            value={getCurrentContent()}
            onChange={(e) => handleContentChange(activeLanguage, e?.target?.value, activeContentType)}
          />
        </div>
      </div>
    </div>
  );
};

export default MemberManagementSection;